import { Grid, Stack, Button, Menu, Dialog, DialogTitle, DialogContent, DialogActions, Box, Divider, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ConfirmDialog } from '../../../../reusable-components/confirm-dialog';
import { getRentalItems, getCategoryData, addItemsToOrder, getResidentsForFacility, getCartsForUser } from '../../../../redux/slices/rentals';
import { useDispatch, useSelector } from '../../../../redux/store';
import Scrollbar from '../../../../reusable-components/scrollbar';
import RentalCartDropdown from './RentalCartDropdown';
import Image from '../../../../reusable-components/image';
import RentalCategoryDropdown from './RentalCategoryDropdown';
import RentalItem from './RentalItem';
import {postAddNewRentalOrder, punchoutOrder, getFacilityName, getRentalPunchoutVendors} from '../../../../api/rentals';
import RentalSearch from './RentalSearch';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import RentalSubcategorySelect from './RentalSubcategorySelect';
import Iconify from '../../../../reusable-components/iconify';
import { AwaitButton } from '../../../../reusable-components/await-button';
import { ScopeGuard } from '../../../../reusable-components/scopes';

export default function RentalItemsSelect() {
  const dispatch = useDispatch();
  const {
    data: { items, categoryData, facilityVendors, residentsList, cart },
  } = useSelector((state) => state.rentals);
  const siteId = useSelector((state) => state.user.user.siteId);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();  
  const [searchParams] = useSearchParams();
  const selectedCategoryName = searchParams.get('category');
  const [selectedCategory, setSelectedCategory] = useState(categoryData.find((cat) => cat.categoryName === selectedCategoryName));
  const selectedCategoryId = selectedCategory?.categoryId;
  const selectedResident = searchParams.get('residentId');
  const selectedFacility = searchParams.get('facilityId');
  const isExistingOrder = searchParams.get('isExistingOrder') === 'true';
  const date = searchParams.get('date') ? new Date(searchParams.get('date')) : null;
  const orderId = searchParams.get('orderId');
  const facilityId = searchParams.get('facilityId');
  const orderStatus = searchParams.get('orderStatus') || 'new-rentals';
  const [debouncedValue, setDebouncedValue] = useState('');
  const [resident, setResident] = useState('');
  const [facilityName, setFacilityName] = useState('');
  const [subcategories, setSubcategories] = useState([]);
  const [itemsToAdd, setItemsToAdd] = useState([]);
  const [isBouncing, setIsBouncing] = useState(false);
  const [currentCart, setCurrentCart] = useState(null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [punchingOut, setPunchingOut] = useState(false);
  const [confirmPunchout, setConfirmPunchout] = useState(null);
  const [punchoutVendors, setPunchoutVendors] = useState([]);
  useEffect(() => {
    dispatch(getRentalItems());
    dispatch(getResidentsForFacility(facilityId))
    dispatch(getCartsForUser());
    dispatch(getCategoryData());  
  },[])
  
   useEffect(() => {
    if (selectedCategoryName && categoryData.length > 0)
     setSelectedCategory(categoryData.find((cat) => cat.categoryName === selectedCategoryName));
   },[selectedCategoryName, categoryData])

  useEffect(() => {
    if(residentsList.length > 0 && selectedResident)
      setResident(residentsList.find((resident) => resident.id === parseInt(selectedResident, 10)));
  },[residentsList, selectedResident])

  useEffect(() => {
  const loadFacility = async () => {
    const res = await getFacilityName(selectedFacility);
    setFacilityName(res);
  };

  if (selectedFacility) {
    loadFacility();
  }
}, [selectedFacility]);

  useEffect(() => {
    if(cart.length > 0 && selectedResident)
      setCurrentCart(cart.filter((cartItem) => cartItem.residentId === parseInt(selectedResident, 10)));
  }, [cart, selectedResident, debouncedValue]);

  const sortItems = (a, b) => {
    const aPref = a.isPreferred ? 0 : 1;
    const bPref = b.isPreferred ? 0 : 1;
    const aPurchase = a.pricingTypeId === 3 ? 1 : 0;
    const bPurchase = b.pricingTypeId === 3 ? 1 : 0;
    if (aPref !== bPref) return aPref - bPref;
    if (aPurchase !== bPurchase) return aPurchase - bPurchase;
    const dateA = new Date(a.dateAdded);
    const dateB = new Date(b.dateAdded);
    return dateB - dateA;
  }

  const itemsJoinedWithVendor = useMemo( 
    () =>  items
        .filter((item) => facilityVendors.some((fv) => fv.vendorId === item.vendorId))
        .map((item) => {
          const matchingVendor = facilityVendors.find((fv) => fv.vendorId === item.vendorId);
          const cartEntry = orderId !== null || isExistingOrder ? itemsToAdd?.find(i => i.itemId === item.itemId) 
            : currentCart?.find( (c) => c.itemId === item.itemId );
          const selectedPricingType = cartEntry?.pricingTypeId === item.pricingTypeId
            ? null
            : item.pricingTypes?.find((pricingType) => pricingType.pricingType === cartEntry?.pricingTypeId);
          return {
            ...item,
            price: cartEntry?.price ?? selectedPricingType?.price ?? item.price,
            pricingTypeId: cartEntry?.pricingTypeId ?? item.pricingTypeId,
            pricingTypeName: cartEntry?.pricingTypeName ?? selectedPricingType?.pricingTypeName ?? item.pricingTypeName,
            isPreferred: matchingVendor.isPreferred,
            quantity: cartEntry ? cartEntry?.quantity : 0,
            cartItemId: cartEntry ? cartEntry?.cartItemId : null,
          };
        })
        .sort((a, b) => sortItems(a,b))
    , [currentCart, items, facilityVendors, debouncedValue, itemsToAdd]
  );
  
  const filteredItems = useMemo( 
    () => itemsJoinedWithVendor.filter((item) => {
        const matchesCategory = item.categoryId === selectedCategoryId;
        const matchesSubcategory = !subcategories.length || (subcategories.length === 1 && subcategories[0] === 'rentalsOnly') ? true : subcategories.includes(item.subcategoryName);
        const matchesRentals = subcategories.includes('rentalsOnly') ? item.pricingTypeId !== 3  && item.description !== "Respiratory Therapist Visit" : true;
        const matchesSearch = item?.description?.toLowerCase().includes(debouncedValue?.toLowerCase());
        return matchesCategory && matchesSubcategory && matchesSearch && matchesRentals;
      }), [itemsJoinedWithVendor, selectedCategoryId, subcategories]
  );

  const updateOrder = () => {
    dispatch(addItemsToOrder(itemsToAdd, orderId, orderStatus ));    
    navigate(`/dashboard/rentals/rental-order/${orderId}/${orderStatus}`);
  }
 
  const addNewOrder = () => {
    postAddNewRentalOrder(itemsToAdd, selectedResident, selectedCategoryId, date ).then(res => {
    navigate(`/dashboard/rentals/rental-order/${res?.orderId}/current`);
  });}

  const punchout = (vendorId, vendorName) => {
    setPunchingOut(true);
    punchoutOrder(selectedResident, vendorId, siteId, orderId).then( res => {
      if(res.data.successUrl){
       const newWin = window.open(res.data.successUrl);
       if (!newWin || newWin.closed || typeof newWin.closed === 'undefined'){
         setConfirmPunchout({ url: res.data.successUrl, vendor: vendorName });
         setPunchingOut(false);
       }
      }
      else{
        enqueueSnackbar(`Punchout Error: ${res.data.errorMessage || "Punchout Failed"}`, { variant: 'error' });
        setPunchingOut(false);
      }
    }).catch(() => {
       enqueueSnackbar(`Something has gone wrong`, { variant: 'error' });
       setPunchingOut(false);
    });
  }
  const handleCancel = () => {
    navigate(`/dashboard/rentals/rental-order/${orderId}/${orderStatus}`);
  };

  const handleActionsClick = (event) => {
    const anchorEl = event.currentTarget;
    getRentalPunchoutVendors(resident.facilityId).then((res) => {
      if (res == null || res.length === 0) {
        enqueueSnackbar(`No punchout vendors available for this facility`, { variant: 'error' });
      }
      else{
        setActionsAnchorEl(anchorEl);
        setPunchoutVendors(res);
      }
  })
  };

   const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };
  
  function renderOrderPreviewBox(message) {
    return (
      <Box sx={{ p: 2, minWidth: 350, maxWidth: 500 }}>
        {message !== "" && <><Box>{message}</Box>
        <Divider sx={{ mb: 2, mt: 2 }} /></>}
        <Box>
          <Typography
            variant="h6"
            sx={{ textAlign: "center", fontWeight: "bold", color: "info.main", mb: 2 }}
          >
            Order Preview
          </Typography>
          <Stack spacing={1}>
            <Typography>
              <span style={{ fontWeight: 'bold', color: "#208ee0" }}>Facility: </span>
              {facilityName ? `${facilityName}` : "N/A"}
            </Typography>
            <Typography>
              <span style={{ fontWeight: 'bold', color: "#208ee0" }}>Resident: </span>
              {resident ? `${resident?.firstName} ${resident?.lastName}` : "N/A"}
            </Typography>
            <Typography>
              <span style={{ fontWeight: 'bold', color: "#208ee0" }}>Category: </span>
              {selectedCategory ? selectedCategory.categoryName : "N/A"}
            </Typography>
            {date && <Typography>
                      <span style={{ fontWeight: 'bold', color: "#208ee0" }}>Rental Start Date: </span>
                      {date?.toLocaleDateString()}
                    </Typography>
            }
            <Box>
              <Typography>
                <span style={{ fontWeight: 'bold', color: "#208ee0" }}>Items:</span>
              </Typography>
              {itemsToAdd.length === 0 ? (
                <Typography variant="body2">No items selected</Typography>
              ) : (
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {itemsToAdd.map((item) => (
                    <li key={item.itemId}>
                      <span>{item.description || `Item ${item.itemId}`}</span> (<strong>Qty:</strong> {item.quantity})
                    </li>
                  ))}
                </Box>
              )}
            </Box>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Rental Items | Platform Solutions</title>
      </Helmet>
        <Dialog
          maxWidth="md"
          open={confirmPunchout != null}
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            setConfirmPunchout(null);
          }}
        >
          <DialogTitle sx={{ pb: 2 }}>Browser has blocked this puchout link</DialogTitle>
          <DialogContent sx={{ typography: 'body2' }}>
            <Box>Your browser has blocked this punchout link to {confirmPunchout?.vendor} from automatically opening.</Box>
            <Box>Please click open link to go to the punchout.</Box>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => { window.open(confirmPunchout?.url); setConfirmPunchout(null); }}>
              Open link
            </Button>
            <Button variant="outlined" color="inherit" onClick={() => setConfirmPunchout(null)}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
        <Stack style={{ width: '100%' }}>
        <Grid container direction="row" spacing={1} mt={3} alignItems={'center'}>
          <Grid item sx={{ ml: 6, mt: 5, color: '#8cc53f', fontWeight: 'bold', }}>
            <span style={{ fontWeight: 'bold' }}>Facility:</span> <span style={{ fontWeight: 'normal' }}>{facilityName}</span>
          </Grid>
          <Grid item sx={{ ml: 6, mt: 5, color: '#8cc53f', fontWeight: 'bold', }}>
            <span style={{ fontWeight: 'bold' }}>Resident:</span> <span style={{ fontWeight: 'normal' }}>{resident?.firstName} {resident?.lastName}</span>
          </Grid>

          <Grid item sx={{ ml: 6, mt: 5, color: '#8cc53f', fontWeight: 'bold', }}>
          <ScopeGuard superAdminOnly>
           {
            !isExistingOrder && !orderId &&
            <Stack direction='row' justifyContent='flex-start'>
              <Button onClick={handleActionsClick} color="info" variant="text" sx={{ textDecoration: 'underline' }} endIcon={<Iconify icon="mingcute:down-line" width={16} height={16} />}>
                Punchout Order
              </Button>
              <Menu anchorEl={actionsAnchorEl} open={Boolean(actionsAnchorEl)} onClose={handleActionsClose}>
                {
                  punchoutVendors.map((vendor) => (
                    <AwaitButton key={vendor.vendorId} onClick={() => punchout(vendor.vendorId, vendor.vendorName)} loading={punchingOut} color="secondary" icon="ion:open-outline" width={16} height={16} ml={1} sx={{ mr: 1 }} >
                      <Image alt="vendor logo" src={vendor.logoUrl} style={{ width: '100px', height: 30, objectFit: 'contain' }} />
                      <Typography variant="subtitle1" sx={{ ml: 1, color: "black !important" }}>
                        {vendor.vendorName}
                      </Typography>
                    </AwaitButton>
                  ))
                }
              </Menu>
            </Stack>
          }
          </ScopeGuard>
          </Grid>
          <Grid item sx={{ flexGrow: 1 }} />
          <Grid item sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <RentalSearch debouncedValue={debouncedValue} setDebouncedValue={setDebouncedValue} />
          </Grid>
          { !isExistingOrder && !orderId && <Grid item sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <RentalCartDropdown isBouncing={isBouncing} />
          </Grid>}
        </Grid>
        <Grid container direction="row" alignItems="center">
          <Grid item sx={{ ml: 4.5, mt: 2 }}>
            <RentalCategoryDropdown setSubcategories={setSubcategories} orderId={orderId} orderStatus={orderStatus} isExistingOrder={isExistingOrder} />
          </Grid>
          <Grid item sx={{ ml: 6, mt: 3 }}>
            <RentalSubcategorySelect subcategories={subcategories} setSubcategories={setSubcategories} />
          </Grid>
          <Grid item xs />
          <Grid item>
            {(orderId || isExistingOrder) && (
              <Stack direction='row' justifyContent='flex-end' alignItems="center" spacing={1} sx={{ mr: 2, mt: 2, mb: 2 }}>
                {orderId && (
                  <>
                    <Button sx={{ mr: 1 }} size="small" variant="outlined" color="error" onClick={handleCancel}>
                      Cancel
                    </Button>
                    {orderStatus === 'pending' ? (
                      <ConfirmDialog 
                        button 
                        title = "Add Items to Order" 
                        actionButton = "Add to Order" 
                        content = {renderOrderPreviewBox("")} 
                        color="primary" 
                        icon = 'save'
                        action={updateOrder}
                        buttonStyles={{ marginRight: '10px', color: 'secondary.main', border: '1px solid rgba(177, 40, 188, 0.5)' }}/>
                    ) : (
                      <ConfirmDialog
                        button
                        title="Add Items and Send to Vendor"
                        icon="save-and-send"
                        actionButton="Add items and Send to vendor"
                        color="primary"
                        content={renderOrderPreviewBox(`This will add the selected items to the current rental order and email the updated order to the vendor.`)}
                        action={() => updateOrder()}
                        buttonStyles={{ marginRight: '10px', color: 'secondary.main', border: '1px solid rgba(177, 40, 188, 0.5)' }} />
                    )}
                  </>
                )}
                {(isExistingOrder) && (
                  <ConfirmDialog
                    button
                    icon='save'
                    title="Add Order"
                    color="primary"
                    content={renderOrderPreviewBox('This order will be added to the current rental table without being emailed out to the vendor.')}
                    actionButton="Submit"
                    action={() => addNewOrder()}
                    buttonStyles={{ marginRight: '10px', color: 'secondary.main', border: '1px solid rgba(177, 40, 188, 0.5)' }}
                  />
                )}
              </Stack>
            )}
          </Grid>
        </Grid>
        <Scrollbar sx={{ height: 'calc(100vh - 275px)' }}>
          <Grid container spacing={4} sx={{ justifyContent: 'center', alignItems: 'flex-start', ml: 3 }}>
            {filteredItems.length === 0 ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '80px' }}>
                <p>No items available for this category.</p>
              </div>
            ) : (
              filteredItems.length > 0 ? filteredItems : itemsJoinedWithVendor).map((item) => (
                <RentalItem key={item.itemId} item={item} orderId={orderId} setItemsToAdd={setItemsToAdd} itemsToAdd={itemsToAdd} setIsBouncing={setIsBouncing} isExistingOrder={isExistingOrder} cartId={currentCart?.length ? currentCart[0]?.cartId : 0} />
              )
            )}{' '}
          </Grid>
        </Scrollbar>
      </Stack>
    </>
  );
}
